import React, { useState, useEffect } from 'react';
import { Platform, View } from 'react-native';
import { SvgXml } from 'react-native-svg';

interface HyzoneMascotProps {
  width?: number;
  height?: number;
  verticalOffset?: number; // Vertical offset in pixels (positive = down, negative = up)
}

// Global cache for SVG content - persists across all component instances
let cachedSvgContent: string | null = null;
let loadingPromise: Promise<string> | null = null;

// Load SVG once and cache it globally
const loadSvgContent = async (): Promise<string> => {
  // If already cached, return immediately
  if (cachedSvgContent) {
    return cachedSvgContent;
  }

  // If already loading, wait for the existing promise
  if (loadingPromise) {
    return loadingPromise;
  }

  // Start loading
  loadingPromise = (async () => {
    try {
      if (Platform.OS === 'web') {
        // For web, fetch from the public folder (copied there during build)
        const response = await fetch('/hyzonemascot.svg');
        if (response.ok) {
          const text = await response.text();
          cachedSvgContent = text;
          return text;
        }
        throw new Error('Failed to fetch mascot SVG');
      } else {
        // For native, use expo-asset to load the file
        const { Asset } = require('expo-asset');
        const mascotAsset = require('../../assets/hyzonemascot.svg');
        const asset = Asset.fromModule(mascotAsset);
        await asset.downloadAsync();
        const response = await fetch(asset.localUri || asset.uri);
        const text = await response.text();
        cachedSvgContent = text;
        return text;
      }
    } catch (err) {
      console.warn('Failed to load mascot SVG:', err);
      throw err;
    }
  })();

  return loadingPromise;
};

// Pre-load the SVG immediately when the module loads (only in browser context)
if (typeof window !== 'undefined' && Platform.OS === 'web') {
  loadSvgContent().catch(() => { });
}

export default function HyzoneMascot({ width = 24, height = 24, verticalOffset = -15 }: HyzoneMascotProps) {
  const [svgContent, setSvgContent] = useState<string | null>(cachedSvgContent);

  useEffect(() => {
    // If already cached, use it immediately
    if (cachedSvgContent) {
      setSvgContent(cachedSvgContent);
      return;
    }

    // Otherwise, wait for it to load
    loadSvgContent().then(setSvgContent).catch(() => { });
  }, []);

  if (!svgContent) {
    // Return a placeholder with the same dimensions to prevent layout shift
    return Platform.OS === 'web' ? (
      <div style={{ width, height, display: 'inline-flex', marginTop: verticalOffset }} />
    ) : (
      <View style={{ width, height, marginTop: verticalOffset }} />
    );
  }

  // Replace width and height in the SVG while preserving viewBox
  let svgXml = svgContent.replace(/width="[^"]*"/, `width="${width}"`);
  svgXml = svgXml.replace(/height="[^"]*"/, `height="${height}"`);
  svgXml = svgXml.replace(/preserveAspectRatio="[^"]*"/, 'preserveAspectRatio="xMidYMid meet"');

  if (Platform.OS === 'web') {
    return (
      <div
        style={{
          width: width,
          height: height,
          display: 'inline-flex',
          marginTop: verticalOffset,
        }}
        dangerouslySetInnerHTML={{ __html: svgXml }}
      />
    );
  }

  return (
    <View style={{ marginTop: verticalOffset }}>
      <SvgXml xml={svgXml} width={width} height={height} />
    </View>
  );
}
